import { apiClient } from './api/client';

function decodeBase64Url(value) {
  const normalized = String(value ?? '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return Uint8Array.from(window.atob(padded), (character) => character.charCodeAt(0));
}

function encodeBase64Url(value) {
  if (value == null) return null;
  const bytes = new Uint8Array(value);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function creationOptions(publicKey) {
  return {
    ...publicKey,
    challenge: decodeBase64Url(publicKey.challenge),
    user: { ...publicKey.user, id: decodeBase64Url(publicKey.user.id) },
    excludeCredentials: (publicKey.excludeCredentials ?? []).map((credential) => ({
      ...credential,
      id: decodeBase64Url(credential.id),
    })),
  };
}

function requestOptions(publicKey) {
  return {
    ...publicKey,
    challenge: decodeBase64Url(publicKey.challenge),
    allowCredentials: (publicKey.allowCredentials ?? []).map((credential) => ({
      ...credential,
      id: decodeBase64Url(credential.id),
    })),
  };
}

function registrationCredential(credential) {
  return {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      attestationObject: encodeBase64Url(credential.response.attestationObject),
      clientDataJSON: encodeBase64Url(credential.response.clientDataJSON),
      transports: typeof credential.response.getTransports === 'function'
        ? credential.response.getTransports()
        : [],
    },
  };
}

function authenticationCredential(credential) {
  return {
    id: credential.id,
    rawId: encodeBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      authenticatorData: encodeBase64Url(credential.response.authenticatorData),
      clientDataJSON: encodeBase64Url(credential.response.clientDataJSON),
      signature: encodeBase64Url(credential.response.signature),
      userHandle: encodeBase64Url(credential.response.userHandle),
    },
  };
}

export function passkeysAvailable() {
  return Boolean(
    typeof window !== 'undefined'
    && window.isSecureContext
    && window.PublicKeyCredential
    && navigator.credentials,
  );
}

export function getPasskeyStatus() {
  return apiClient.get('/auth/passkeys/status');
}

export async function createPasskey() {
  if (!passkeysAvailable()) throw new Error('Passkeys are not available on this device.');
  const begin = await apiClient.post('/auth/passkeys/registration/options', {});
  const credential = await navigator.credentials.create({
    publicKey: creationOptions(begin.publicKey),
  });
  if (!credential) throw new Error('Passkey creation was cancelled.');
  return apiClient.post('/auth/passkeys/registration/verify', {
    challengeId: begin.challengeId,
    credential: registrationCredential(credential),
  });
}

export async function signInToMeetingWithPasskey(qrToken) {
  if (!passkeysAvailable()) throw new Error('Passkeys are not available on this device.');
  const begin = await apiClient.post(
    '/api/meetings/passkey/options',
    qrToken ? { qrToken } : {},
    { skipAuth: true, skipAuthRefresh: true },
  );
  const credential = await navigator.credentials.get({
    publicKey: requestOptions(begin.publicKey),
  });
  if (!credential) throw new Error('Passkey sign-in was cancelled.');
  return apiClient.post(
    '/api/meetings/passkey/verify',
    {
      challengeId: begin.challengeId,
      credential: authenticationCredential(credential),
    },
    { skipAuth: true, skipAuthRefresh: true },
  );
}
